// Server functions for File operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import crypto from 'crypto'
import { PutObjectCommand, DeleteObjectCommand, CopyObjectCommand } from '@aws-sdk/client-s3'
import { S3_BUCKET_NAME, s3Client } from '@/src/server/lib/s3'
import { generateShareId } from '@/src/server/lib/share'
import { getSignedUrlProcedure } from '@/src/server/lib/s3/getSignedUrl'
import { getThumbnailUrls } from '@/src/lib/thumbnails'
import { isUuid } from '@/src/lib/uuid'
import { getFileKey, getIsometricKey, getPdfKey, getThumbnailKey } from '@/shared/lib/s3Keys'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { PERMISSIONS } from '@/permission.config'
import { hasPermission } from '@/src/server/lib/permissions'
import { isAdmin, requireUserId } from '@/src/server/lib/access'
import { buildFileWhere, canAccessFile } from '@/src/server/lib/accessControl'
import {
  getFilesForList,
  getUserFilesForList,
  countFiles,
  countUserFiles
} from '@/src/server/lib/queries/fileQueries'
import { z } from 'zod'
import { logger } from '@/src/server/lib/logger'

// Zod schemas for input validation
const ExistsByHashInput = z.object({
  hash: z.string(),
})

const GetFileByShareIdInput = z.object({
  shareId: z.string(),
})

const GetFileByHashInput = z.object({
  hash: z.string(),
})

const ListFilesInput = z.object({
  all: z.boolean().optional(),
  search: z.string().optional(),
  userId: z.string().optional(),
  projectId: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  orderBy: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
}).optional()

const ListFilesByOrganizationInput = z.object({
  orgId: z.string(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  orderBy: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
})

const SearchFilesInput = z.object({
  search: z.string().optional(),
  userId: z.string().optional(),
  projectId: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
}).optional()

const DeleteFileInput = z.object({
  shareId: z.string(),
})

const RollFileShareIdInput = z.object({
  fileId: z.string(),
})

const GetSignedUrlInput = z.object({
  key: z.string(),
})

const UpdateIsometricViewInput = z.object({
  fileId: z.string(),
  imageBase64: z.string(),
})

const GetDrawingsForFileInput = z.object({
  fileId: z.string(),
})

const GetDrawingsForFileByShareIdInput = z.object({
  shareId: z.string(),
})

const GetFilesForDrawingInput = z.object({
  drawingId: z.string(),
})

function buildUserFileOrderBy(orderBy?: string, order?: 'asc' | 'desc') {
  const direction = order || 'desc'
  switch (orderBy) {
    case 'name':
      return { file: { name: direction } }
    case 'privacy':
      return { privacy: direction }
    case 'createdAt':
      return { createdAt: direction }
    case 'shareId':
      return { file: { shareId: direction } }
    case 'user':
      return { user: { name: direction } }
    default:
      return { createdAt: direction }
  }
}

function buildFileOrderBy(orderBy?: string, order?: 'asc' | 'desc') {
  const direction = order || 'desc'
  switch (orderBy) {
    case 'name':
      return { name: direction }
    case 'createdAt':
      return { createdAt: direction }
    case 'shareId':
      return { shareId: direction }
    case 'projectId':
      return { projects: { _count: direction } }
    case 'drawings':
      return { drawings: { _count: direction } }
    case 'users':
    case 'userCount':
      return { userFiles: { _count: direction } }
    default:
      return { createdAt: direction }
  }
}

function normalizeR2Key(input: string): string {
  if (input.startsWith('files/')) return input
  if (input.startsWith('http://') || input.startsWith('https://')) {
    try {
      const url = new URL(input)
      const filesIndex = url.pathname.indexOf('/files/')
      if (filesIndex >= 0) {
        return url.pathname.slice(filesIndex + 1)
      }
    } catch {
      // Fall through to raw input
    }
  }
  if (input.startsWith('/files/')) return input.slice(1)
  return input
}

// SECURITY: This endpoint should only be called from server-side code
// It's used for file deduplication during upload
// Rate limiting is handled at the upload level
export const existsByHash = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ExistsByHashInput.parse(data))
  .handler(async ({ data }) => {
    // Note: This is intentionally simple for performance
    // Access control is enforced at the upload endpoint level
    // which is the only legitimate caller of this function
    const file = await db.file.findFirst({ where: { hash: data.hash } })
    return !!file
  })

export const getFileByShareId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetFileByShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const isAdminUser = await hasPermission(session, [PERMISSIONS.ADMIN])

    // Support both shareId and database ID
    // UUID format: 8-4-4-4-12 (with hyphens)
    // ShareId format: F followed by alphanumeric (no hyphens)
    const isShareIdLookup = !isUuid(data.shareId)
    const whereClause = isShareIdLookup
      ? { shareId: data.shareId }
      : { id: data.shareId }

    const file = await (db as any).file.findFirst({
      where: whereClause,
      include: {
        userFiles: {
          where: { deletedAt: null },
          include: { user: { select: { id: true, email: true, name: true } } },
        },
        drawings: {
          select: {
            id: true,
            name: true,
            shareId: true,
          },
          ...((!isAdminUser && userId) ? {
            where: {
              userId,
            },
          } : {}),
        },
      },
    })
    if (!file) return null
    const userFile = userId ? file.userFiles.find((uf: any) => uf.userId === userId) : undefined
    
    // SECURITY: ShareID access allows viewing the file (having the shareId IS authorization).
    // For direct ID access, require ownership or admin.
    if (!isShareIdLookup && !userFile && !isAdminUser) return null

    const { userFiles, drawings, ...fileData } = file

    // Generate signed URL for the file (prefer converted PDF if available)
    const fileKey = getFileKey(file.id, file.name)
    const isPdf = (file.type || '').toLowerCase() === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    const preferredKey = isPdf ? fileKey : getPdfKey(file.id, file.name)
    const preferredSigned = await getSignedUrlProcedure(preferredKey)
    const signedUrlResult = preferredSigned ?? await getSignedUrlProcedure(fileKey)

    return {
      ...fileData,
      signedUrl: signedUrlResult?.url || null,
      createdAt: (userFile?.createdAt ?? file.createdAt) instanceof Date
        ? (userFile?.createdAt ?? file.createdAt).toISOString()
        : (userFile?.createdAt ?? file.createdAt),
      privacy: userFile?.privacy ?? 'LOW',
      uploadedBy: userFile?.user ?? file.userFiles[0]?.user ?? null,
      users: isAdminUser
        ? userFiles.map((uf: any) => ({
          user: uf.user,
          createdAt: uf.createdAt instanceof Date ? uf.createdAt.toISOString() : uf.createdAt,
        }))
        : undefined,
      drawings: drawings || [],
    }
  })

export const getFileByHash = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetFileByHashInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const isAdminUser = await isAdmin(session)
    const file = await (db as any).file.findFirst({
      where: {
        hash: data.hash,
        ...(isAdminUser ? {} : { userFiles: { some: { userId, deletedAt: null } } }),
      },
    })
    if (!file) throw new Error(`File with hash '${data.hash}' not found.`)
    return { ...file, createdAt: file.createdAt instanceof Date ? file.createdAt.toISOString() : file.createdAt }
  })

export const listFiles = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListFilesInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id

    // Admin mode - shows files based on permission level
    if (data?.all) {
      // Check if user has permission to see other users' files
      const isGlobalAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
      const canListAllFiles = await hasPermission(session, [PERMISSIONS.FILES_LIST_ALL, PERMISSIONS.ADMIN])
      
      if (!canListAllFiles) {
        throw new Error('Unauthorized')
      }

      // Build base conditions
      const andConditions: any[] = []
      
      // If not global admin, filter by organization
      // IMPORTANT: Filter by organizationId on UserFile, not by user's current org membership
      if (!isGlobalAdmin && userId) {
        // Org admin/owner: only see files that belong to their organization
        const user = await db.user.findUnique({
          where: { id: userId },
          select: { organizationId: true },
        })
        
        if (user?.organizationId) {
          // Filter to files that belong to this organization (by organizationId on UserFile)
          andConditions.push({
            userFiles: {
              some: {
                organizationId: user.organizationId,
                deletedAt: null,
              },
            },
          })
        } else {
          // No organization - shouldn't have FILES_LIST_ALL permission
          throw new Error('Unauthorized')
        }
      }
      
      // Apply additional filters
      if (data?.userId) andConditions.push({ userFiles: { some: { userId: data.userId, deletedAt: null } } })
      if (data?.projectId) andConditions.push({ projects: { some: { id: data.projectId } } })
      if (data?.search) {
        andConditions.push({
          OR: [
            { name: { contains: data.search, mode: 'insensitive' } },
            { description: { contains: data.search, mode: 'insensitive' } },
            { id: { contains: data.search, mode: 'insensitive' } },
            { shareId: { contains: data.search, mode: 'insensitive' } },
          ],
        })
      }

      let where: any = undefined
      if (andConditions.length === 1) {
        where = andConditions[0]
      } else if (andConditions.length > 1) {
        where = { AND: andConditions }
      }

      const orderBy = buildFileOrderBy(data?.orderBy, data?.order)

      const [files, total] = await Promise.all([
        getFilesForList({
          where,
          orderBy,
          skip: data?.offset ?? 0,
          take: data?.limit ?? 20,
        }),
        countFiles(where),
      ])

      return { files, total }
    }

    // Regular mode - only current user's files
    if (!userId) {
      return { files: [], total: 0 }
    }

    const userFileWhere: any = { deletedAt: null }
    if (data?.projectId) {
      userFileWhere.file = { projects: { some: { id: data.projectId } } }
    }
    if (data?.search) {
      userFileWhere.file = {
        ...(userFileWhere.file || {}),
        OR: [
          { name: { contains: data.search, mode: 'insensitive' } },
          { description: { contains: data.search, mode: 'insensitive' } },
          { id: { contains: data.search, mode: 'insensitive' } },
          { shareId: { contains: data.search, mode: 'insensitive' } },
        ],
      }
    }

    const orderBy = buildUserFileOrderBy(data?.orderBy, data?.order)

    const [files, total] = await Promise.all([
      getUserFilesForList({
        userId,
        where: userFileWhere,
        orderBy,
        skip: data?.offset ?? 0,
        take: data?.limit ?? 100,
      }),
      countUserFiles({ userId, deletedAt: null, ...userFileWhere }),
    ])

    return { files, total }
  })

export const listFilesByOrganization = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => ListFilesByOrganizationInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdminUser = await isAdmin(session)
    const userId = session?.user?.id
    
    if (!isAdminUser) {
      if (!userId) throw new Error('Unauthorized')
      const user = await db.user.findUnique({ 
        where: { id: userId }, 
        include: { roleDetails: true },
      })
      if (!user || user.organizationId !== data.orgId) throw new Error('Unauthorized')
      
      // Require FILES_LIST_ALL for org-level file listing
      const userPermissions = user?.roleDetails?.permissions || []
      if (!userPermissions.includes('FILES_LIST_ALL') && !userPermissions.includes('*')) {
        throw new Error('Unauthorized: Organization file listing requires admin access')
      }
    }
    const { orgId, limit, offset, orderBy, order } = data
    // IMPORTANT: Filter by organizationId on UserFile, not by user membership
    const where = { AND: [{ deletedAt: null }, { organizationId: orgId }] }
    const total = await (db as any).userFile.count({ where })
    const userFiles = await (db as any).userFile.findMany({
      where,
      include: {
        user: { select: { id: true, name: true, email: true } },
        file: { include: { projects: { select: { id: true, name: true } }, drawings: { select: { id: true } } } },
      },
      skip: offset ?? 0,
      take: limit ?? 100,
      orderBy: buildUserFileOrderBy(orderBy, order),
    })
    return {
      files: userFiles.map((uf: any) => ({
        ...uf.file,
        createdAt: uf.createdAt.toISOString(),
        privacy: uf.privacy,
        user: uf.user ? { id: uf.user.id, name: uf.user.name, email: uf.user.email } : null,
        uploadedBy: uf.user ? { id: uf.user.id, name: uf.user.name, email: uf.user.email } : null,
        projects: uf.file.projects || [],
        drawingIds: uf.file.drawings ? uf.file.drawings.map((d: any) => d.id) : [],
      })),
      total,
    }
  })

export const searchFiles = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => SearchFilesInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const isUserAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])

    // SECURITY: Require authentication
    if (!userId && !isUserAdmin) {
      throw new Error('Authentication required')
    }

    // Use centralized buildFileWhere for consistent privacy rules
    const baseWhere = await buildFileWhere(userId, isUserAdmin, {
      includeOrg: true,
      projectId: data?.projectId,
      search: data?.search,
    })

    const where = baseWhere
    const [userFiles, total] = await Promise.all([
      (db as any).userFile.findMany({
        where,
        include: {
          user: { select: { id: true, name: true, email: true } },
          file: { include: { projects: { select: { id: true, name: true } }, drawings: { select: { id: true } } } },
        },
        skip: data?.offset ?? 0,
        take: data?.limit ?? 20,
        orderBy: { createdAt: 'desc' },
      }),
      (db as any).userFile.count({ where }),
    ])
    return {
      files: userFiles.map((uf: any) => ({
        ...uf.file,
        createdAt: uf.createdAt.toISOString(),
        privacy: uf.privacy,
        user: uf.user ? { id: uf.user.id, name: uf.user.name, email: uf.user.email } : null,
        uploadedBy: uf.user ? { id: uf.user.id, name: uf.user.name, email: uf.user.email } : null,
        projects: uf.file.projects || [],
        drawingIds: uf.file.drawings ? uf.file.drawings.map((d: any) => d.id) : [],
      })),
      total,
    }
  })

export const deleteFile = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteFileInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    if (!userId) throw new Error('Unauthorized')

    // Check if user has hard delete permission (admin-level)
    const canHardDelete = await hasPermission(session, [PERMISSIONS.FILES_HARD_DELETE, PERMISSIONS.ADMIN])

    const file = await db.file.findUnique({
      where: { shareId: data.shareId },
      include: { drawings: true }
    })
    if (!file) throw new Error('File not found.')

    if (canHardDelete) {
      // Hard delete - permanently remove file and all related data

      // Delete all related drawings first
      if (file.drawings && file.drawings.length > 0) {
        const drawingIds = file.drawings.map((d: any) => d.id)
        await db.drawing.deleteMany({ where: { id: { in: drawingIds } } })
      }

      // Delete files from R2 storage
      if (S3_BUCKET_NAME) {
        const filesToDelete: string[] = [
          getFileKey(file.id, file.name),        // Original file
          getPdfKey(file.id, file.name),          // Converted PDF (if exists)
          getThumbnailKey(file.id, 'small'),      // Small thumbnail
          getThumbnailKey(file.id, 'large'),      // Large thumbnail
          getIsometricKey(file.id),               // Extracted/replaced isometric view
        ]

        // Delete all files (ignore errors for files that don't exist)
        for (const key of filesToDelete) {
          try {
            await s3Client.send(new DeleteObjectCommand({ Bucket: S3_BUCKET_NAME, Key: key }))
          } catch (error) {
            // Silently ignore - file might not exist
            console.log(`Skipped deleting ${key} (may not exist)`)
          }
        }
      }

      // Delete file record from database
      await db.file.delete({ where: { id: file.id } })
    } else {
      // Soft delete - mark UserFile relationship as deleted
      // Requires ownership OR FILES_SOFT_DELETE permission (for org files in same org)
      
      // First, try to find user's own file link
      let userFile = await (db as any).userFile.findFirst({
        where: { fileId: file.id, userId, deletedAt: null }
      })
      
      // If not owner, check if user has FILES_SOFT_DELETE permission for org files
      if (!userFile) {
        const canSoftDeleteOrgFiles = await hasPermission(session, [PERMISSIONS.FILES_SOFT_DELETE])
        
        if (canSoftDeleteOrgFiles) {
          // Get user's organization
          const user = await db.user.findUnique({
            where: { id: userId },
            select: { organizationId: true },
          })
          
          if (user?.organizationId) {
            // Find any UserFile for this file that belongs to the same organization
            userFile = await (db as any).userFile.findFirst({
              where: { 
                fileId: file.id, 
                organizationId: user.organizationId,
                deletedAt: null 
              }
            })
          }
        }
      }
      
      if (!userFile) throw new Error('File link not found or no permission to delete.')
      await (db as any).userFile.update({
        where: { id: userFile.id },
        data: { deletedAt: new Date() }
      })
    }

    // Cache invalidation disabled - Prisma Accelerate not configured
    // await db.$accelerate.invalidate({ tags: ['fileList', 'fileListAll', 'fileListByOrgId', 'fileGetByShareId'] })
    return { success: true }
  })

export const deleteFileHard = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteFileInput.parse(data))
  .handler(async ({ data }) => {
    // Deprecated: Use deleteFile instead, which handles admin permissions automatically
    const session = await getSessionFromRequest()
    if (!await hasPermission(session, [PERMISSIONS.ADMIN])) {
      throw new Error('Unauthorized')
    }
    const file = await db.file.findUnique({
      where: { shareId: data.shareId },
      include: { drawings: true }
    })
    if (!file) throw new Error('File not found.')

    // Delete all related drawings first
    if (file.drawings && file.drawings.length > 0) {
      const drawingIds = file.drawings.map((d: any) => d.id)
      await db.drawing.deleteMany({ where: { id: { in: drawingIds } } })
    }

    // Delete files from R2 storage
    if (S3_BUCKET_NAME) {
      const filesToDelete: string[] = [
        getFileKey(file.id, file.name),        // Original file
        getPdfKey(file.id, file.name),          // Converted PDF (if exists)
        getThumbnailKey(file.id, 'small'),      // Small thumbnail
        getThumbnailKey(file.id, 'large'),      // Large thumbnail
        getIsometricKey(file.id),               // Extracted/replaced isometric view
      ]

      // Delete all files (ignore errors for files that don't exist)
      for (const key of filesToDelete) {
        try {
          await s3Client.send(new DeleteObjectCommand({ Bucket: S3_BUCKET_NAME, Key: key }))
        } catch (error) {
          // Silently ignore - file might not exist
          console.log(`Skipped deleting ${key} (may not exist)`)
        }
      }
    }

    await db.file.delete({ where: { id: file.id } })
    // Cache invalidation disabled - Prisma Accelerate not configured
    // await db.$accelerate.invalidate({ tags: ['fileList', 'fileListAll', 'fileListByOrgId', 'fileGetByShareId'] })
    return { success: true }
  })

export const rollFileShareId = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RollFileShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const file = await db.file.findUnique({ where: { id: data.fileId } })
    if (!file) throw new Error('File not found.')
    const newShareId = await generateShareId({ prefix: 'F', length: 8, isIdAvailable: async (id) => !(await db.file.findUnique({ where: { shareId: id } })) })
    // No need to change R2 key since it uses database id, not shareId
    await db.file.update({ where: { id: data.fileId }, data: { shareId: newShareId } })
    return { shareId: newShareId }
  })

export const getSignedUrl = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetSignedUrlInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdminUser = await isAdmin(session)

    const normalizedKey = normalizeR2Key(data.key)
    const isIsometric = normalizedKey.includes('/isometric.png')
    
    // Log isometric view requests for debugging
    if (isIsometric) {
      logger.info({ key: normalizedKey, userId: session?.user?.id }, '[ISOMETRIC] getSignedUrl request')
    }
    
    if (!isAdminUser) {
      const userId = session?.user?.id
      
      // Extract fileId from the key to check authorization
      // Keys follow patterns:
      // - files/{fileId}/{filename}
      // - files/{fileId}/thumb_small.png
      // - files/{fileId}/thumb_large.png
      const keyMatch = normalizedKey.match(/^files\/([^/]+)/)
      if (!keyMatch) throw new Error('Invalid key format')
      
      const fileId = keyMatch[1]
      
      // Use canAccessFile which handles:
      // - Direct ownership
      // - Organization access with privacy levels (LOW = org members can view)
      // - Project sharing
      const hasAccess = await canAccessFile(fileId, userId, isAdminUser)
      
      if (!hasAccess) throw new Error('Unauthorized')
    }

    const result = await getSignedUrlProcedure(normalizedKey)
    
    // Log isometric view result for debugging
    if (isIsometric) {
      logger.info({ 
        key: normalizedKey, 
        found: result !== null,
        hasUrl: result?.url ? true : false 
      }, '[ISOMETRIC] getSignedUrl result')
    }
    
    return result
  })

export const updateIsometricView = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpdateIsometricViewInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const isAdminUser = await isAdmin(session)

    if (!userId && !isAdminUser) {
      throw new Error('Unauthorized')
    }

    const hasAccess = await canAccessFile(data.fileId, userId, isAdminUser)
    if (!hasAccess) {
      throw new Error('Unauthorized')
    }

    const file = await db.file.findUnique({
      where: { id: data.fileId },
      select: { id: true },
    })
    if (!file) {
      throw new Error('File not found')
    }

    const key = getIsometricKey(file.id)

    if (!S3_BUCKET_NAME) {
      throw new Error('Storage bucket not configured')
    }

    if (!data.imageBase64.trim()) {
      await s3Client.send(new DeleteObjectCommand({
        Bucket: S3_BUCKET_NAME,
        Key: key,
      }))

      logger.info({ fileId: file.id, key, actorUserId: userId }, '[ISOMETRIC] Isometric view deleted manually')

      return { success: true, key, deleted: true }
    }

    const imageBuffer = Buffer.from(data.imageBase64, 'base64')
    if (!imageBuffer.length) {
      throw new Error('Invalid image payload')
    }

    const maxImageSizeBytes = 10 * 1024 * 1024
    if (imageBuffer.length > maxImageSizeBytes) {
      throw new Error('Image too large (max 10MB)')
    }

    await s3Client.send(new PutObjectCommand({
      Bucket: S3_BUCKET_NAME,
      Key: key,
      Body: imageBuffer,
      ContentType: 'image/png',
      CacheControl: 'no-cache, no-store, must-revalidate',
    }))

    logger.info({ fileId: file.id, key, actorUserId: userId }, '[ISOMETRIC] Isometric view replaced manually')

    return { success: true, key, deleted: false }
  })

export const getDrawingsForFile = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetDrawingsForFileInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdminUser = await isAdmin(session)
    if (!isAdminUser) {
      const userId = requireUserId(session)
      const link = await (db as any).userFile.findFirst({ where: { fileId: data.fileId, userId, deletedAt: null } })
      if (!link) throw new Error('Unauthorized')
    }
    return await db.drawing.findMany({ where: { files: { some: { id: data.fileId } } } })
  })

export const getDrawingsForFileByShareId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetDrawingsForFileByShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdminUser = await isAdmin(session)
    const file = await db.file.findFirst({ where: { shareId: data.shareId } })
    if (!file) throw new Error('File not found')

    if (!isAdminUser) {
      const userId = requireUserId(session)
      const link = await (db as any).userFile.findFirst({ where: { fileId: file.id, userId, deletedAt: null } })
      if (!link) throw new Error('Unauthorized')
    }

    const drawings = await (db as any).drawing.findMany({
      where: { files: { some: { id: file.id } } },
      include: {
        files: { select: { id: true, name: true, shareId: true } },
        master: true,
      },
    })

    const { getEffectiveDrawingData } = await import('@/src/server/lib/drawingMerge')

    return drawings.map((d: any) => ({
      ...d,
      effectiveData: getEffectiveDrawingData(d),
      createdAt: d.createdAt instanceof Date ? d.createdAt.toISOString() : d.createdAt,
    }))
  })

export const getFilesForDrawing = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetFilesForDrawingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const isAdminUser = await isAdmin(session)
    if (!isAdminUser) {
      const userId = requireUserId(session)
      const drawing = await (db as any).drawing.findFirst({
        where: { id: data.drawingId },
        include: { files: { where: { userFiles: { some: { userId, deletedAt: null } } } } },
      })
      if (!drawing || drawing.files.length === 0) throw new Error('Unauthorized')
      return drawing.files
    }
    return await db.file.findMany({ where: { drawings: { some: { id: data.drawingId } } } })
  })
